import { useState, useEffect, useRef, useCallback } from "react";
import { ChuziApiError } from "@chuzi/shared/api";
import {
  CONTENT_RATING_IDS,
  t,
} from "@chuzi/shared/config";
import type {
  ContentRating,
  RealmId,
  StoryListItem,
  StoryVisibilityTier,
} from "@chuzi/shared/types";
import { resolveStoryVisibilityTier } from "@chuzi/shared/types";
import { api } from "../lib/api";

const GENRES = [
  "Action",
  "Adventure",
  "Animation",
  "Biography",
  "Comedy",
  "Crime",
  "Documentary",
  "Drama",
  "Family",
  "Fantasy",
  "Film-Noir",
  "History",
  "Horror",
  "Music",
  "Musical",
  "Mystery",
  "Romance",
  "Sci-Fi",
  "Sport",
  "Thriller",
  "War",
  "Western",
];

type PublishFormVariant = "publish" | "metadata";

interface PublishFormProps {
  story: StoryListItem;
  realm: RealmId;
  variant?: PublishFormVariant;
  onPublished: () => void;
  onCancel: () => void;
}

export function PublishForm({
  story,
  realm,
  variant = "publish",
  onPublished,
  onCancel,
}: PublishFormProps) {
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(90,180,90," : "rgba(126,184,255,";

  const [title, setTitle] = useState(story.title);
  const [rating, setRating] = useState<ContentRating | "">(
    story.content_rating ?? ""
  );
  const [genre, setGenre] = useState(story.genre ?? "");
  const [tags, setTags] = useState<string[]>(story.tags ?? []);
  const [tagInput, setTagInput] = useState("");
  const [publishTier, setPublishTier] = useState<StoryVisibilityTier>(
    () => resolveStoryVisibilityTier(story),
  );
  const [allTags, setAllTags] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const q = tagInput.trim();
    api.tags
      .index(q ? { q } : undefined)
      .then((res) => {
        if (!cancelled) setAllTags(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tagInput]);

  const filteredTags = allTags
    .filter((t) => !tags.includes(t))
    .sort((a, b) => a.localeCompare(b))
    .slice(0, 10);

  const addTag = useCallback(
    (tag: string) => {
      const normalized = tag.trim().toLowerCase();
      if (!normalized || tags.includes(normalized)) return;
      setTags((prev) => [...prev, normalized]);
      setTagInput("");
      setShowDropdown(false);
      inputRef.current?.focus();
    },
    [tags]
  );

  function removeTag(tag: string) {
    setTags((prev) => prev.filter((t) => t !== tag));
  }

  function handleTagKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (tagInput.trim()) {
        addTag(tagInput);
      }
    } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
    e.stopPropagation();
  }

  async function handleSubmit() {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError(`${t(realm, "editor_film_title")} is required.`);
      return;
    }

    const pendingTag = tagInput.trim().toLowerCase();
    const resolvedTags =
      pendingTag && !tags.includes(pendingTag)
        ? [...tags, pendingTag]
        : tags;

    const requiresPublishMetadata =
      !story.is_system && variant === "publish" && publishTier !== "private";

    if (!story.is_system && (variant !== "publish" || requiresPublishMetadata)) {
      if (!rating) {
        setError(`${t(realm, "content_rating")} is required.`);
        return;
      }
      if (!genre) {
        setError(`${t(realm, "genre")} is required.`);
        return;
      }
    }

    if (!story.is_system && requiresPublishMetadata && resolvedTags.length === 0) {
      setError(t(realm, "tags_required", "At least one tag is required."));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const tierPayload =
        variant === "publish"
          ? publishTier === "worldwide"
            ? { published: true, soft_published: false }
            : publishTier === "limited"
              ? { published: false, soft_published: true }
              : { published: false, soft_published: false }
          : {};

      await api.stories.update(story.id, {
        title: trimmedTitle,
        content_rating: rating ? (rating as ContentRating) : story.content_rating,
        genre: genre || story.genre,
        ...(variant === "publish"
          ? { tags: resolvedTags, ...tierPayload }
          : { tags: resolvedTags.length > 0 ? resolvedTags : story.tags }),
      });
      onPublished();
    } catch (err) {
      if (err instanceof ChuziApiError) {
        if (err.status === 429) {
          setError(t(realm, "error_rate_limited", "Too many requests. Wait a moment and try again."));
          setSubmitting(false);
          return;
        }
        const body = err.body as
          | { message?: string; errors?: Record<string, string[]> }
          | null;
        const firstFieldError = body?.errors
          ? Object.values(body.errors).flat()[0]
          : null;
        setError(
          firstFieldError ?? body?.message ?? `Publish failed (${err.status})`
        );
      } else {
        setError(err instanceof Error ? err.message : "Publish failed");
      }
      setSubmitting(false);
    }
  }

  function handleFormKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      onCancel();
    }
    e.stopPropagation();
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: `${accent}0.08)`,
    border: `1px solid ${accent}0.3)`,
    borderRadius: 6,
    color: "#e8f0ff",
    padding: "7px 10px",
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "rgba(232,240,255,0.5)",
    marginBottom: 3,
    display: "block",
  };

  return (
    <div
      style={{
        position: "absolute",
        right: "calc(50% - 320px)",
        top: "50%",
        transform: "translateY(-50%)",
        width: 280,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        zIndex: 30,
        padding: 16,
        background: isWilds ? "rgba(20,40,20,0.85)" : "rgba(10,16,32,0.85)",
        border: `1px solid ${accent}0.3)`,
        borderRadius: 10,
        backdropFilter: "blur(12px)",
      }}
      onKeyDown={handleFormKeyDown}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 1.5,
          color: `${accent}0.6)`,
          marginBottom: -4,
        }}
      >
        {variant === "metadata"
          ? t(realm, "story_metadata_heading")
          : t(realm, "publish_form_heading")}
      </div>

      {/* Title */}
      <div>
        <label style={labelStyle}>{t(realm, "editor_film_title")} *</label>
        <input
          type="text"
          value={title}
          maxLength={255}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t(realm, "film_title_placeholder")}
          style={inputStyle}
        />
      </div>

      {!story.is_system ? (
        <>
      {/* Rating */}
      <div>
        <label style={labelStyle}>{t(realm, "content_rating")} *</label>
        <select
          value={rating}
          onChange={(e) => setRating(e.target.value as ContentRating | "")}
          style={inputStyle}
        >
          <option value="">{t(realm, "select_content_rating")}</option>
          {CONTENT_RATING_IDS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      {/* Genre */}
      <div>
        <label style={labelStyle}>{t(realm, "genre")} *</label>
        <select
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
          style={inputStyle}
        >
          <option value="">{t(realm, "select_genre")}</option>
          {GENRES.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </div>

      {/* Tags */}
      <div>
        <label style={labelStyle}>
          {t(realm, "tags_label")}
          {variant === "publish" ? " *" : ""}
        </label>

        {/* Selected tag pills */}
        {tags.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              marginBottom: 6,
            }}
          >
            {tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: `${accent}0.2)`,
                  border: `1px solid ${accent}0.4)`,
                  color: "#e8f0ff",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "rgba(232,240,255,0.5)",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: 12,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}

        {/* Tag input with dropdown */}
        <div style={{ position: "relative" }}>
          <input
            ref={inputRef}
            type="text"
            value={tagInput}
            onChange={(e) => {
              setTagInput(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => {
              setTimeout(() => setShowDropdown(false), 200);
            }}
            onKeyDown={handleTagKeyDown}
            placeholder={t(realm, "tags_placeholder")}
            style={inputStyle}
            maxLength={80}
          />

          {showDropdown && filteredTags.length > 0 ? (
            <div
              ref={dropdownRef}
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                maxHeight: 180,
                overflowY: "auto",
                background: isWilds
                  ? "rgba(20,40,20,0.95)"
                  : "rgba(10,16,32,0.95)",
                border: `1px solid ${accent}0.3)`,
                borderTop: "none",
                borderRadius: "0 0 6px 6px",
                zIndex: 40,
              }}
            >
              {filteredTags.map((tag) => (
                <TagOption
                  key={tag}
                  tag={tag}
                  accent={accent}
                  onSelect={() => addTag(tag)}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
        </>
      ) : null}

      {variant === "publish" ? (
        <div>
          <label style={labelStyle}>{t(realm, "publish_form_heading")}</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(["private", "limited", "worldwide"] as const).map((tier) => (
              <label
                key={tier}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  color: "#e8f0ff",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="publish-tier"
                  value={tier}
                  checked={publishTier === tier}
                  onChange={() => setPublishTier(tier)}
                />
                {t(realm, `publish_tier_${tier}`)}
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {error ? (
        <div
          style={{
            fontSize: 11,
            color: "#ff9aa2",
            background: "rgba(255,154,162,0.08)",
            border: "1px solid rgba(255,154,162,0.4)",
            borderRadius: 6,
            padding: "6px 8px",
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
        <button
          onClick={onCancel}
          disabled={submitting}
          style={{
            flex: 1,
            padding: "7px 0",
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(232,240,255,0.6)",
            background: "transparent",
            border: `1px solid ${accent}0.25)`,
            borderRadius: 6,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t(realm, "director_profile_cancel")}
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            flex: 1,
            padding: "7px 0",
            fontSize: 12,
            fontWeight: 700,
            color: "#e8f0ff",
            background: `${accent}0.35)`,
            border: `1px solid ${accent}0.5)`,
            borderRadius: 6,
            cursor: submitting ? "wait" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {submitting
            ? t(realm, "director_profile_saving")
            : variant === "metadata"
              ? t(realm, "story_metadata_save")
              : t(realm, "hud_publish_control")}
        </button>
      </div>
    </div>
  );
}

function TagOption({
  tag,
  accent,
  onSelect,
}: {
  tag: string;
  accent: string;
  onSelect: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: hovered ? `${accent}0.12)` : "transparent",
        border: "none",
        color: "#e8f0ff",
        padding: "6px 10px",
        fontSize: 12,
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "background 150ms",
      }}
    >
      {tag}
    </button>
  );
}
